import { useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, AlertTriangle } from "lucide-react";
import { useT } from "../i18n";
import { USE_CASES, href } from "../content/shared";
import { PageHero, Reveal, Chat, MaskHeadline, ArrowLink } from "../components/kit";
import { DownloadBand } from "./Home";
import { Demo } from "../components/Demo";

const UC_DEMO = { "blocked-driveway": "photo", "lights-left-on": "lights", "parking-coordination": "coord", "open-window-or-door": "photo", "workplace-parking": "audio", "travel-parking": "video", "vehicle-issue": "voice", "shared-residential-parking": "scene" };

export function UseCases() {
  const { lang, t } = useT();
  const u = t.useCases;
  return (
    <>
      <PageHero eyebrow={u.eyebrow} lines={u.h1} lead={u.lead} testid="usecases-hero" />
      <section className="container-x pb-20" data-testid="usecases-grid">
        <div className="grid gap-x-8 gap-y-14 md:grid-cols-2">
          {USE_CASES.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 2) * 0.08} className={i % 2 ? "md:mt-20" : ""}>
              <Link to={href(lang, "useCases", c.slug)} className="group block" data-testid={`usecase-card-${c.slug}`}>
                <div className="overflow-hidden rounded-[1.5rem]">
                  <img src={c.img} alt={u.items[c.slug].title} loading={i < 2 ? "eager" : "lazy"} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-sm text-muted-foreground">{String(i + 1).padStart(2, "0")}</p>
                    <h2 className="mt-1 text-2xl md:text-3xl">{u.items[c.slug].title}</h2>
                    <p className="mt-2 max-w-md text-muted-foreground">{u.items[c.slug].short}</p>
                  </div>
                  <ArrowUpRight className="mt-6 h-6 w-6 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-primary" aria-hidden />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      <DownloadBand />
    </>
  );
}

export function UseCase() {
  const { slug } = useParams();
  const { lang, t } = useT();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "15%"]);
  const meta = USE_CASES.find((c) => c.slug === slug);
  if (!meta) return <Navigate to={href(lang, "useCases")} replace />;
  const u = t.useCases;
  const c = u.items[slug];
  const L = u.labels;
  const related = USE_CASES.filter((x) => x.slug !== slug).slice(0, 3);
  return (
    <article data-testid={`usecase-page-${slug}`}>
      <section className="container-x pt-10 md:pt-16">
        <Link to={href(lang, "useCases")} className="text-sm text-muted-foreground hover:text-primary" data-testid="usecase-back-link">← {t.nav.useCases}</Link>
        <div className="mt-8 grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-end">
          <MaskHeadline lines={c.h1} testid="usecase-title" />
          <Reveal delay={0.3}><p className="lead">{c.short}</p></Reveal>
        </div>
      </section>
      <section ref={ref} className="container-x mt-12">
        <div className="relative overflow-hidden rounded-[2rem]">
          <motion.img style={{ y }} src={meta.img} alt={c.photoAlt} className="aspect-[4/5] w-full scale-110 object-cover sm:aspect-[16/9] lg:aspect-[21/9]" fetchpriority="high" />
          <div className="absolute bottom-4 right-4 hidden w-[340px] sm:block md:bottom-8 md:right-8"><Chat plate={meta.plate} messages={c.chat} testid="usecase-hero-chat" /></div>
        </div>
        <div className="mt-6 sm:hidden"><Chat plate={meta.plate} messages={c.chat} testid="usecase-hero-chat-mobile" /></div>
      </section>
      <section className="container-x section grid gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="eyebrow mb-4">{L.situation}</p>
          <p className="font-display text-2xl font-medium leading-snug md:text-3xl">{c.situation}</p>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="eyebrow mb-4">{L.problem}</p>
          <p className="text-lg text-muted-foreground">{c.problem}</p>
        </Reveal>
      </section>
      <section className="border-y border-border bg-surface">
        <div className="container-x section grid gap-12 lg:grid-cols-[1fr_1.2fr]">
          <Reveal>
            <h2>{L.how}</h2>
            <Demo scenario={UC_DEMO[slug]} plate={meta.plate} className="mt-10 max-w-[340px]" testid="usecase-demo" />
          </Reveal>
          <ol className="space-y-6">
            {c.how.map((s, i) => (
              <Reveal as="li" key={i} delay={i * 0.06} className="grid grid-cols-[2.5rem_1fr] gap-4 border-b border-border pb-6 last:border-0">
                <span className="font-mono text-sm text-primary">0{i + 1}</span>
                <span className="text-lg">{s}</span>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>
      <section className="container-x section grid gap-12 lg:grid-cols-[1fr_1.2fr]">
        <Reveal>
          <p className="eyebrow mb-4">{L.example}</p>
          <p className="text-muted-foreground">{L.exampleNote}</p>
          <blockquote className="mt-6 border-l-2 border-primary pl-5 font-display text-xl md:text-2xl">“{c.message}”</blockquote>
        </Reveal>
        <Reveal delay={0.08} className="rounded-2xl border border-signal/60 bg-signal/10 p-7 md:p-9" data-testid="usecase-limits">
          <p className="flex items-center gap-2 font-display text-lg font-semibold"><AlertTriangle className="h-5 w-5" aria-hidden />{L.limits}</p>
          <ul className="mt-4 space-y-3">
            {c.limits.map((l, i) => <li key={i} className="flex gap-3 text-foreground/90"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/60" />{l}</li>)}
          </ul>
        </Reveal>
      </section>
      <section className="container-x pb-20">
        <div className="flex items-end justify-between gap-6 border-t border-border pt-12">
          <h2 className="text-2xl md:text-3xl">{L.related}</h2>
          <ArrowLink to={href(lang, "useCases")} testid="usecase-all-link">{t.common.allUseCases}</ArrowLink>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {related.map((r) => (
            <Link key={r.slug} to={href(lang, "useCases", r.slug)} className="group" data-testid={`usecase-related-${r.slug}`}>
              <div className="overflow-hidden rounded-2xl"><img src={r.img} alt={u.items[r.slug].title} loading="lazy" className="aspect-[3/2] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" /></div>
              <p className="mt-4 font-display text-lg font-semibold group-hover:text-primary">{u.items[r.slug].title}</p>
            </Link>
          ))}
        </div>
      </section>
      <DownloadBand />
    </article>
  );
}
